import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarCheck, Link2Off } from 'lucide-react';
import { errMsg } from '../../lib/aiInterviewApi';
import { schedulingAPI } from '../../lib/schedulingApi';
import { Pill, Section, copyText } from '../aiinterview/recruiter/common';

export default function CalendarConnections({ me, reload }) {
  const [busy, setBusy] = useState(null);
  const conns = Object.fromEntries((me.connections || []).map((c) => [c.provider, c]));
  const connect = async (p) => {
    setBusy(p);
    try {
      const { data } = await schedulingAPI.connectCalendar(p);
      window.location.assign(data.authorize_url);
    } catch (e) {
      toast.error(errMsg(e, 'Could not start the connection'));
      setBusy(null);
    }
  };
  const disconnect = async (p) => {
    setBusy(p);
    try {
      await schedulingAPI.disconnectCalendar(p);
      toast.success('Calendar disconnected');
      reload();
    } catch (e) {
      toast.error(errMsg(e, 'Could not disconnect'));
    } finally {
      setBusy(null);
    }
  };
  const anyConfigured = Object.values(me.providers || {}).some((p) => p.configured);
  return (
    <Section title="Calendar sync" testId="calendar-connections">
      <p className="iv-small" style={{ marginTop: 0 }}>Connect your calendar so busy times are blocked automatically and booked interviews get a Google Meet or Teams link. Without a connection, CHIRE emails an .ics invite instead.</p>
      {Object.entries(me.providers || {}).map(([k, p]) => {
        const c = conns[k];
        return (
          <div key={k} className="sc-provider" data-testid={`calendar-provider-${k}`}>
            <div>
              <b style={{ color: '#002A38' }}>{p.label}</b>
              <div className="iv-small">
                {c ? <>{c.email} · {c.status === 'connected' ? <Pill tone="green">Connected</Pill> : <Pill tone="amber">Reconnect needed</Pill>}</>
                  : p.configured ? 'Not connected' : <Pill tone="slate" testId={`calendar-not-configured-${k}`}>Not set up yet</Pill>}
              </div>
            </div>
            {c ? (
              <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" disabled={busy === k} onClick={() => disconnect(k)} data-testid={`calendar-disconnect-${k}`}><Link2Off size={14} /> Disconnect</button>
            ) : (
              <button type="button" className="iv-btn iv-btn-primary iv-btn-sm" disabled={!p.configured || busy === k} onClick={() => connect(k)} data-testid={`calendar-connect-${k}`}><CalendarCheck size={14} /> Connect</button>
            )}
          </div>
        );
      })}
      {!anyConfigured && (
        <div className="iv-note" style={{ marginTop: '0.75rem' }} data-testid="calendar-setup-note">
          An admin needs to add the Google and/or Microsoft app keys before calendars can connect. Register all of these redirect URIs (this site and production) when creating the apps:
          {Object.entries(me.providers || {}).flatMap(([k, p]) => (p.redirect_uris || [p.redirect_uri]).map((u, i) => (
            <div key={u} className="iv-row" style={{ marginTop: 6, gap: '0.4rem' }}>
              <span className="sc-mono">{u}</span>
              <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => copyText(u, toast)} data-testid={`calendar-copy-redirect-${k}${i ? `-${i}` : ''}`}>Copy</button>
            </div>
          )))}
        </div>
      )}
    </Section>
  );
}

export function useCalendarReturnToast(params, setParams) {
  useEffect(() => {
    const st = params.get('calendar');
    if (!st) return;
    const label = params.get('provider') === 'microsoft' ? 'Microsoft calendar' : 'Google Calendar';
    if (st === 'connected') toast.success(`${label} connected`);
    else if (st === 'denied') toast.error(`${label} access was not granted`);
    else toast.error(`Could not connect ${label}. Please try again.`);
    const next = new URLSearchParams(params);
    ['calendar', 'provider'].forEach((k) => next.delete(k));
    setParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
